import { PagePrompt, Typography } from '@weareopenr/component-library';
import styles from './ErrorBoundary.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { PostHogErrorBoundary } from '@posthog/react';

type ErrorBoundaryVariant = 'page' | 'layout';

interface ErrorPageContentProps {
  buttonText: string;
  onButtonClick: () => void;
  variant?: ErrorBoundaryVariant;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  withRouter?: boolean;
  variant?: ErrorBoundaryVariant;
}

const ErrorPageContent = ({ buttonText, onButtonClick, variant = 'page' }: ErrorPageContentProps) => {
  return (
    <PagePrompt
      title="Oops"
      primaryButton={{
        text: buttonText,
        onClick: onButtonClick,
      }}
      containerClassName={variant === 'layout' ? styles['transparent-container'] : undefined}
    >
      <div className={styles['message-container']}>
        <Typography size="medium" weight="bold">
          Something didn&apos;t go as planned.
        </Typography>
        <Typography size="medium">
          Try again in a moment. If this keeps happening, please contact our support team.
        </Typography>
      </div>
    </PagePrompt>
  );
};

const ErrorPage = ({ variant }: { variant?: ErrorBoundaryVariant }) => {
  const navigate = useNavigate();
  return <ErrorPageContent buttonText="Go to dashboard" onButtonClick={() => navigate('/')} variant={variant} />;
};

const RootErrorPage = () => {
  return <ErrorPageContent buttonText="Reload Page" onButtonClick={() => globalThis.location.reload()} />;
};

const RouterErrorBoundary = ({ children, variant }: { children: React.ReactNode; variant?: ErrorBoundaryVariant }) => {
  const location = useLocation();

  return (
    <PostHogErrorBoundary key={location.key} fallback={<ErrorPage variant={variant} />}>
      {children}
    </PostHogErrorBoundary>
  );
};

const RootErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <PostHogErrorBoundary fallback={<RootErrorPage />}>{children}</PostHogErrorBoundary>
);

export const ErrorBoundary = ({ children, withRouter = true, variant = 'page' }: ErrorBoundaryProps) => {
  if (withRouter) {
    return <RouterErrorBoundary variant={variant}>{children}</RouterErrorBoundary>;
  }
  return <RootErrorBoundary>{children}</RootErrorBoundary>;
};
